/** @fileoverview A dropdown for choice inputs with a filter box at the top of its list; it can also load more options as the viewer scrolls. */

import { useEffect, useRef, useState } from 'react';
import { CheckIcon, ChevronDownIcon, LoaderCircle, Search } from 'lucide-react';
import { cn } from 'cn';

import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Popover, PopoverContent, PopoverTrigger } from '../ui/popover';

/** One option of the dropdown: a stable key and the text it shows. */
export type ChoiceItem = { key: string; label: string };

/* How close to the end of the list, in pixels, a scroll asks for the next page. */
const LOAD_MORE_DISTANCE = 40;

type ChoiceDropdownProps = {
  controlId: string;
  /** Name of the input, for the accessible names of the filter box and the list. */
  inputLabel: string;
  describedBy?: string;
  invalid: boolean;
  required?: boolean;
  disabled?: boolean;
  /** Text the closed dropdown shows when nothing is picked. */
  placeholder: string;
  /** Options to show, already filtered. */
  items: ChoiceItem[];
  selectionPaths?: (string | undefined)[];
  selectionDefinitionPaths?: (string | undefined)[];
  selectedKey?: string;
  /** Text the closed dropdown shows for the picked value. */
  selectedLabel?: string;
  loading: boolean;
  /** Why the options could not load, shown in the list. */
  error?: string;
  filterText: string;
  /**
   * False while the shown items may belong to an older filter text (a search still loading), so
   * Enter does not pick a stale option. Defaults to true, for items filtered in the browser.
   */
  itemsMatchFilter?: boolean;
  onFilterTextChange: (text: string) => void;
  onOpenChange?: (isOpen: boolean) => void;
  /** True when more options can be loaded, with `onLoadMore`. */
  hasMore?: boolean;
  onLoadMore?: () => void;
  onPick: (key: string) => void;
  className?: string;
};

/**
 * Shows the picked option in a button that opens a list with a filter box. The list does not
 * filter by itself: the caller filters the items, in the browser or on the server. A hidden
 * input carries the picked key, so a form's required check and error text work as for a select.
 * @param props the options, the picked value, the filter text and the callbacks
 */
export function ChoiceDropdown({
  controlId,
  inputLabel,
  describedBy,
  invalid,
  required,
  disabled,
  placeholder,
  items,
  selectionPaths,
  selectionDefinitionPaths,
  selectedKey,
  selectedLabel,
  loading,
  error,
  filterText,
  itemsMatchFilter = true,
  onFilterTextChange,
  onOpenChange,
  hasMore = false,
  onLoadMore,
  onPick,
  className,
}: ChoiceDropdownProps) {
  const [isOpen, setIsOpen] = useState(false);
  const [activeIndex, setActiveIndex] = useState(0);
  const filterRef = useRef<HTMLInputElement>(null);
  const listId = `${controlId}-options`;
  const canLoadMore = hasMore && !loading && Boolean(onLoadMore);
  /* The option Enter picks: kept inside the list when the items change. */
  const shownActiveIndex = Math.max(0, Math.min(activeIndex, items.length - 1));
  const activeItem = items[shownActiveIndex];
  const activeOptionId = activeItem ? `${listId}-${shownActiveIndex}` : undefined;

  /* The active option stays in view as the arrow keys move it. */
  useEffect(() => {
    if (isOpen && activeOptionId) {
      document.getElementById(activeOptionId)?.scrollIntoView({ block: 'nearest' });
    }
  }, [activeOptionId, isOpen]);

  /**
   * Opens or closes the list; closing clears the filter text.
   * @param nextOpen whether the list is open
   */
  const changeOpen = (nextOpen: boolean) => {
    setIsOpen(nextOpen);
    if (nextOpen) {
      const selectedIndex = items.findIndex(({ key }) => key === selectedKey);
      setActiveIndex(Math.max(0, selectedIndex));
    }
    onOpenChange?.(nextOpen);
    if (!nextOpen) {
      onFilterTextChange('');
    }
  };

  /**
   * Picks an option and closes the list.
   * @param key the picked option's key
   */
  const pick = (key: string) => {
    onPick(key);
    changeOpen(false);
  };

  /**
   * Moves the active option with the arrow, Home and End keys, and picks it with Enter. The
   * focus stays in the filter box. Enter does nothing while the items may be stale.
   * @param event the key press in the filter box
   */
  const handleFilterKey = (event: React.KeyboardEvent<HTMLInputElement>) => {
    const lastIndex = items.length - 1;
    const nextIndexByKey: Record<string, number> = {
      ArrowDown: Math.min(shownActiveIndex + 1, lastIndex),
      ArrowUp: Math.max(shownActiveIndex - 1, 0),
      Home: 0,
      End: lastIndex,
    };
    if (event.key in nextIndexByKey) {
      event.preventDefault();
      setActiveIndex(Math.max(0, nextIndexByKey[event.key]));
    } else if (event.key === 'Enter') {
      event.preventDefault();
      if (itemsMatchFilter && activeItem) {
        pick(activeItem.key);
      }
    }
  };

  /**
   * Asks for the next page when the list is scrolled near its end.
   * @param event the scroll of the list
   */
  const loadMoreAtEnd = (event: React.UIEvent<HTMLDivElement>) => {
    const { scrollTop, scrollHeight, clientHeight } = event.currentTarget;
    if (canLoadMore && scrollHeight - scrollTop - clientHeight < LOAD_MORE_DISTANCE) {
      onLoadMore?.();
    }
  };

  return (
    <div className="relative">
      <Popover open={isOpen} onOpenChange={changeOpen}>
        <PopoverTrigger
          id={controlId}
          type="button"
          disabled={disabled}
          aria-describedby={describedBy}
          aria-invalid={invalid ? true : undefined}
          aria-haspopup="listbox"
          aria-controls={isOpen ? listId : undefined}
          className={cn(
            "group flex w-full items-center justify-between gap-1.5 rounded-lg border border-input py-2 pr-2 pl-2.5 text-left text-sm whitespace-nowrap transition-colors outline-none select-none focus-visible:border-ring focus-visible:ring-3 focus-visible:ring-ring/50 disabled:cursor-not-allowed disabled:opacity-50 aria-invalid:border-destructive aria-invalid:ring-3 aria-invalid:ring-destructive/20 dark:aria-invalid:border-destructive/50 dark:aria-invalid:ring-destructive/40 [&_svg]:pointer-events-none [&_svg]:shrink-0 [&_svg:not([class*='size-'])]:size-4",
            className
          )}
        >
          {loading && !isOpen && (
            <LoaderCircle className="animate-spin text-muted-foreground" aria-hidden="true" />
          )}
          <span className={cn('flex-1 truncate', !selectedLabel && 'text-muted-foreground')}>
            {selectedLabel || placeholder}
          </span>
          <ChevronDownIcon
            className="text-muted-foreground transition-transform duration-200 group-data-[popup-open]:rotate-180 motion-reduce:transition-none"
            aria-hidden="true"
          />
        </PopoverTrigger>
        <PopoverContent
          initialFocus={filterRef}
          className="w-(--anchor-width) min-w-56 gap-1 p-1 duration-150 ease-out"
        >
          <div className="relative">
            <Search
              className="pointer-events-none absolute top-1/2 left-3 size-4 -translate-y-1/2 text-muted-foreground"
              aria-hidden="true"
            />
            <Input
              ref={filterRef}
              type="search"
              role="combobox"
              aria-label={`Filter ${inputLabel} options`}
              aria-expanded={isOpen}
              aria-autocomplete="list"
              aria-controls={isOpen ? listId : undefined}
              aria-activedescendant={activeOptionId}
              placeholder="Filter options"
              className="h-app-control pl-9"
              value={filterText}
              onChange={(event) => {
                setActiveIndex(0);
                onFilterTextChange(event.target.value);
              }}
              onKeyDown={handleFilterKey}
            />
          </div>
          <div
            id={listId}
            role="listbox"
            tabIndex={-1}
            aria-label={`${inputLabel} options`}
            className="max-h-60 overflow-auto"
            onScroll={loadMoreAtEnd}
          >
            {items.map(({ key, label }, optionIndex) => (
              <Button
                key={key}
                data-fabrik-selection-path={selectionPaths?.[optionIndex]}
                data-fabrik-selection-definition-path={selectionDefinitionPaths?.[optionIndex]}
                id={`${listId}-${optionIndex}`}
                type="button"
                variant="ghost"
                role="option"
                tabIndex={-1}
                aria-selected={key === selectedKey}
                data-active={optionIndex === shownActiveIndex ? true : undefined}
                className="h-auto w-full justify-start px-3 py-2 text-left font-normal whitespace-normal transition-colors data-active:bg-accent data-active:text-accent-foreground"
                onMouseMove={() => setActiveIndex(optionIndex)}
                onClick={() => pick(key)}
              >
                <span className="flex-1">{label}</span>
                {key === selectedKey && <CheckIcon className="size-4" aria-hidden="true" />}
              </Button>
            ))}
            {canLoadMore && (
              <Button
                type="button"
                variant="ghost"
                className="h-auto w-full justify-start px-3 py-2 font-normal text-muted-foreground"
                onClick={onLoadMore}
              >
                Show more
              </Button>
            )}
            {loading && (
              <p className="px-3 py-2 text-sm text-muted-foreground" role="status">
                Loading options…
              </p>
            )}
            {!loading && error && (
              <p className="px-3 py-2 text-sm text-destructive" role="alert">
                {error}
              </p>
            )}
            {!loading && !error && items.length === 0 && (
              <p className="px-3 py-2 text-sm text-muted-foreground" role="status">
                No matching options.
              </p>
            )}
          </div>
        </PopoverContent>
      </Popover>
      <input
        aria-hidden="true"
        tabIndex={-1}
        name={controlId}
        required={required}
        value={selectedKey ?? ''}
        onChange={() => undefined}
        className="sr-only"
      />
    </div>
  );
}
